import React from 'react'
import {
    Drawer,
    DrawerBody,
    DrawerProps,
    DrawerHeader,
    DrawerFooter,
    DrawerContent,
    DrawerOverlay,
    ModalHeaderProps,
} from '@chakra-ui/react'
import { BoxProps } from '@chakra-ui/react'
import { Text, HStack } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { ChevronLeftIcon } from '@chakra-ui/icons'
import { Button, IconButton } from '@chakra-ui/react'

import AlertChanges from '../alert-changes'

interface Props extends DrawerProps {
    title?: string
    children: React.ReactNode
    onCancel?: () => void
    onConfirm?: () => void
    isLoading?: boolean
    isConfirmDisabled?: boolean
    warnBeforeClose?: boolean
    headerOverrides?: ModalHeaderProps
    bodyOverrides?: BoxProps
    footerOverrides?: BoxProps
    footerContent?: React.ReactNode
    showBackButton?: boolean
    headerRightAction?: React.ReactNode
    overlayOverrides?: Omit<
        BoxProps,
        'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'
    >
}

export default function SharedDrawer({
    title,
    children,
    onCancel,
    onConfirm,
    isConfirmDisabled,
    warnBeforeClose,
    headerOverrides,
    bodyOverrides,
    footerOverrides,
    footerContent,
    overlayOverrides,
    showBackButton,
    headerRightAction,
    ...props
}: Readonly<Props>) {
    const { onClose } = props

    const {
        isOpen: isAlertOpen,
        onOpen: onAlertOpen,
        onClose: onAlertClose,
    } = useDisclosure()

    function handleClose() {
        if (warnBeforeClose) {
            onAlertOpen()
        } else {
            onCancel && onCancel()
            onClose()
        }
    }

    function handleConfirm() {
        onConfirm && onConfirm()
        onClose()
    }

    return (
        <>
            <Drawer {...props} onClose={handleClose} blockScrollOnMount={false}>
                <DrawerOverlay {...overlayOverrides} />
                <DrawerContent w={'80vw'}>
                    {(title || showBackButton || headerRightAction) && (
                        <DrawerHeader
                            display="flex"
                            justifyContent="space-between"
                            flexDir="row"
                            {...headerOverrides}
                        >
                            <HStack spacing={4}>
                                {showBackButton && (
                                    <IconButton
                                        size="sm"
                                        icon={<ChevronLeftIcon fontSize="xl" />}
                                        variant="outline"
                                        rounded="full"
                                        aria-label="Close Modal"
                                        onClick={handleClose}
                                    />
                                )}
                                {title && (
                                    <Text
                                        fontWeight="semibold"
                                        fontSize="lg"
                                        noOfLines={1}
                                    >
                                        {title}
                                    </Text>
                                )}
                            </HStack>
                            {headerRightAction}
                        </DrawerHeader>
                    )}
                    <DrawerBody {...bodyOverrides}>{children}</DrawerBody>
                    {(onConfirm || onCancel || footerContent) && (
                        <DrawerFooter
                            justifyContent="flex-start"
                            gridGap={4}
                            {...footerOverrides}
                        >
                            {onConfirm && (
                                <Button
                                    colorScheme="brand"
                                    isDisabled={isConfirmDisabled}
                                    isLoading={props.isLoading}
                                    onClick={handleConfirm}
                                >
                                    Confirm
                                </Button>
                            )}
                            {onCancel && (
                                <Button
                                    onClick={handleClose}
                                    isDisabled={props.isLoading}
                                >
                                    Cancel
                                </Button>
                            )}
                            {footerContent}
                        </DrawerFooter>
                    )}
                </DrawerContent>
            </Drawer>
            {warnBeforeClose && (
                <AlertChanges
                    isOpen={isAlertOpen}
                    onClose={onAlertClose}
                    onDiscard={() => {
                        onCancel && onCancel()
                        onClose()
                    }}
                />
            )}
        </>
    )
}
